import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { Article } from '@/types';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { SectionHeader } from '@/components/SectionHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { FramedImage } from '@/components/FramedImage';
import { Markdown } from '@/components/Markdown';
import { ArticleView, EditorialCard } from '@/components/ArticleView';
import { LoadingState, EmptyState } from '@/components/States';
import { EDIT_CATEGORIES } from '@/content/articles';
import { useI18n } from '@/client/i18n/I18nContext';
import { localizeArticle } from '@/lib/i18n-content';
import { EDIT_CATEGORY_SHOP } from '@/lib/articles';
import { editCategoryPageText, staticPageText, type EditCategorySlug } from '@/lib/seo';
import { lp } from '@/client/i18n/locale';
import { useLogicalParams } from '@/client/i18n/useLogicalParams';

/**
 * Link state of the category filters. RootLayout skips its scroll-to-top for it and
 * the page brings the filter row to the top instead, so the articles stay in view.
 */
export const EDIT_FILTER_STATE = { editFilter: true } as const;

export function isEditFilterNav(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { editFilter?: unknown }).editFilter === true;
}

export function TheEditPage() {
  const { category } = useLogicalParams<'category'>('/the-edit/:category');
  const { t, locale } = useI18n();
  const location = useLocation();
  // Keyed by category: a filter change shows the loading state, never the previous list.
  const [loaded, setLoaded] = useState<{ category?: string; list: Article[] } | null>(null);
  const articles = loaded && loaded.category === category ? loaded.list : null;
  const filtersRef = useRef<HTMLElement>(null);
  const scrolledFor = useRef<string | null>(null);
  const isCat = EDIT_CATEGORIES.some((c) => c.slug === category);
  const catLabel = isCat ? t(`edit.cat.${category}`) : undefined;

  // Same <title>/description as the Worker (lib/seo).
  useMeta({
    ...(isCat
      ? editCategoryPageText(category as EditCategorySlug, locale, t(`edit.catIntro.${category}`))
      : staticPageText('/the-edit', locale)),
    path: category ? `/the-edit/${category}` : '/the-edit',
  });

  useEffect(() => {
    let current = true;
    const done = (list: Article[]) => current && setLoaded({ category, list });
    api.articles(category).then(done).catch(() => done([]));
    return () => {
      current = false;
    };
  }, [category]);

  // After a filter click, once the new list is in (the page has its final height),
  // put the filter row just under the sticky header. Once per navigation.
  useEffect(() => {
    const nav = filtersRef.current;
    if (articles === null || !nav || !isEditFilterNav(location.state) || scrolledFor.current === location.key) return;
    scrolledFor.current = location.key;
    const header = document.querySelector('.header')?.getBoundingClientRect().height ?? 0;
    // Instant (html has scroll-behavior: smooth): the header condenses as soon as the
    // page leaves the top and an animated scroll would land off target.
    window.scrollTo({ top: Math.max(0, nav.getBoundingClientRect().top + window.scrollY - header - 16), behavior: 'instant' });
  }, [articles, location.key, location.state]);

  return (
    <div className="container">
      <Breadcrumb items={[{ name: t('common.home'), path: '/' }, { name: 'Edit', path: '/the-edit' }, ...(catLabel ? [{ name: catLabel }] : [])]} />
      {category ? (
        <>
          <SectionHeader as="h1" eyebrow={catLabel ? t('edit.eyebrow') : undefined} title={catLabel ?? 'Edit'} lede={catLabel ? t('edit.lede') : t('journal.introLede')} />
          {/* The category's intro (also its meta description and the prerender's text). */}
          {isCat ? (
            <div className="prose" style={{ maxWidth: 'var(--width-narrow)', marginBottom: 'var(--space-6)' }}>
              <Markdown source={t(`edit.catIntro.${category}`)} />
            </div>
          ) : null}
        </>
      ) : (
        // Index: no visible title (the h1 stays for SEO / screen readers); intro on
        // the left, Carolina's photo on the right (photo first on phones).
        <div className="media-split">
          <div className="media-split__text prose">
            <h1 className="visually-hidden">Edit</h1>
            <Markdown source={t('journal.intro')} />
          </div>
          <div className="media-split__media">
            <FramedImage
              src="/media/caro/edit-carolina-collaer-trench-y-bufanda-1040.webp"
              alt={t('edit.imageAlt')}
              fallback="CARO"
              sizes="(min-width: 900px) 45vw, 100vw"
            />
          </div>
        </div>
      )}

      <nav ref={filtersRef} style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-6)' }}>
        <Link className="badge" to={lp('/the-edit')} state={EDIT_FILTER_STATE}>{t('nav.all')}</Link>
        {EDIT_CATEGORIES.map((c) => (
          <Link key={c.slug} className="badge" to={lp(`/the-edit/${c.slug}`)} state={EDIT_FILTER_STATE}>
            {t(`edit.cat.${c.slug}`)}
          </Link>
        ))}
      </nav>

      {(() => {
        const shop = category ? EDIT_CATEGORY_SHOP[category] : undefined;
        return shop ? (
          <p style={{ marginBottom: 'var(--space-6)' }}>
            <Link className="btn" to={lp(shop.href)}>
              {t(shop.key)}
            </Link>
          </p>
        ) : null;
      })()}

      {articles === null ? (
        <LoadingState />
      ) : articles.length === 0 ? (
        <EmptyState title={t('edit.emptyTitle')} message={t('edit.emptyMsg')} />
      ) : (
        <div className="editorial-grid editorial-grid--hub" style={{ paddingBottom: 'var(--space-8)' }}>
          {articles.map((raw) => (
            <EditorialCard key={raw.id} article={localizeArticle(raw, locale)} sizes="(min-width: 1100px) 25vw, (min-width: 768px) 50vw, 100vw" />
          ))}
        </div>
      )}
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useLogicalParams<'slug'>('/the-edit/post/:slug');
  const { t, locale } = useI18n();
  const [article, setArticle] = useState<Article | null>(null);
  const [missing, setMissing] = useState(false);

  // Localized view for display + SEO (falls back to Spanish per field).
  const a = article ? localizeArticle(article, locale) : null;

  useMeta({
    // buildTitle() already appends "· CARO. Casa de estilo", so pass the plain
    // title (the seoTitle field keeps its own suffix and would double it).
    title: a?.title ?? t('meta.article.title'),
    description: a?.seoDescription ?? a?.excerpt,
    path: `/the-edit/post/${slug}`,
    type: 'article',
  });

  useEffect(() => {
    if (!slug) return;
    setArticle(null);
    api.article(slug).then(setArticle).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <EmptyState title={t('article.notFound')} />;
  if (!a) return <LoadingState />;

  return (
    <article className="container" style={{ paddingBottom: 'var(--space-8)' }}>
      <Breadcrumb items={[{ name: t('common.home'), path: '/' }, { name: 'Edit', path: '/the-edit' }, { name: a.title }]} />
      <ArticleView article={a} locale={locale} share />
    </article>
  );
}
